import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  Activity,
  BarChart3,
  Boxes,
  ArrowLeftRight,
  BadgeCheck,
  CalendarClock,
  ClipboardCheck,
  Percent,
  Scissors,
  Trash2,
  PackageCheck,
  Building2,
  Coins,
  Contact,
  HandCoins,
  ChevronDown,
  ClipboardList,
  KeyRound,
  LayoutGrid,
  LogOut,
  Menu,
  Package,
  Receipt,
  ScrollText,
  Settings2,
  ShieldCheck,
  ShoppingCart,
  Store,
  Truck,
  UserRound,
  Users,
  Warehouse,
  X,
} from 'lucide-react';
import { BUSINESS_TYPE_LABELS, type Permission } from '@tengesa/shared';
import { api } from '../lib/api';
import { useSession } from './session';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  show: boolean;
}

export const BRANCH_STORAGE_KEY = 'tengesa.branch';

export function useCurrentBranch() {
  const { me } = useSession();
  const [branchId, setBranchId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(BRANCH_STORAGE_KEY);
    } catch {
      return null;
    }
  });
  const valid = me.branches.find((b) => b.id === branchId) ?? me.branches.find((b) => b.id === me.user.defaultBranchId) ?? me.branches[0];
  const select = (id: string) => {
    setBranchId(id);
    try {
      localStorage.setItem(BRANCH_STORAGE_KEY, id);
    } catch {
      /* storage unavailable: selection lasts for this visit */
    }
  };
  return { branch: valid ?? null, select };
}

export function Shell() {
  const { me, can, canAny, hasModule } = useSession();
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setNavOpen(false), [location.pathname]);

  const has = (...p: Permission[]) => canAny(...p);
  const nav: { group: string; items: NavItem[] }[] = [
    {
      group: 'Business',
      items: [
        { to: '/', label: can('dashboard.view') ? 'Dashboard' : 'Home', icon: <LayoutGrid className="size-[18px]" />, show: true },
      ],
    },
    {
      group: 'Selling',
      items: [
        { to: '/pos', label: 'Till', icon: <ShoppingCart className="size-[18px]" />, show: has('pos.sell') },
        { to: '/sales', label: 'Sales', icon: <Receipt className="size-[18px]" />, show: has('pos.sell', 'reports.sales', 'pos.return', 'pos.void_sale') },
        { to: '/shifts', label: 'Till shifts', icon: <Coins className="size-[18px]" />, show: has('shifts.operate', 'shifts.reconcile') },
        { to: '/orders', label: 'Orders and lay-by', icon: <ClipboardCheck className="size-[18px]" />, show: hasModule('orders') && has('orders.manage', 'deliveries.manage', 'customers.view') },
        { to: '/deliveries', label: 'Deliveries', icon: <PackageCheck className="size-[18px]" />, show: hasModule('deliveries') && has('deliveries.manage') },
        { to: '/promotions', label: 'Promotions', icon: <Percent className="size-[18px]" />, show: hasModule('promotions') && has('promotions.manage') },
        { to: '/customers', label: 'Customers', icon: <Contact className="size-[18px]" />, show: has('customers.view', 'customers.manage', 'credit.receive_payment') },
      ],
    },
    {
      group: 'Money',
      items: [
        { to: '/expenses', label: 'Expenses', icon: <HandCoins className="size-[18px]" />, show: has('expenses.record', 'expenses.approve', 'reports.financial') },
        { to: '/exchange-rates', label: 'Exchange rates', icon: <ArrowLeftRight className="size-[18px]" />, show: has('currency.rates_manage', 'settings.manage', 'reports.financial') && me.business.acceptedCurrencies.length > 1 },
        { to: '/reports', label: 'Reports', icon: <BarChart3 className="size-[18px]" />, show: has('reports.sales', 'reports.profit', 'reports.stock', 'reports.financial') },
      ],
    },
    {
      group: 'Stock',
      items: [
        { to: '/products', label: 'Products', icon: <Package className="size-[18px]" />, show: has('products.view', 'products.manage', 'stock.view') },
        { to: '/purchasing', label: 'Purchasing', icon: <Truck className="size-[18px]" />, show: has('purchases.manage', 'stock.receive') },
        { to: '/stock', label: 'Stock control', icon: <Warehouse className="size-[18px]" />, show: has('stock.view', 'stock.adjust', 'stock.transfer') },
        { to: '/expiry', label: 'Expiry dates', icon: <CalendarClock className="size-[18px]" />, show: hasModule('expiry') && has('expiry.manage', 'stock.view') },
        { to: '/breakdowns', label: 'Carcass breakdown', icon: <Scissors className="size-[18px]" />, show: hasModule('yields') && has('yields.manage', 'reports.stock') },
        { to: '/wastage', label: 'Wastage', icon: <Trash2 className="size-[18px]" />, show: hasModule('wastage') && has('wastage.record', 'reports.stock') },
        { to: '/stocktakes', label: 'Stocktakes', icon: <ClipboardList className="size-[18px]" />, show: has('stock.stocktake', 'stock.stocktake_approve') },
        { to: '/suppliers', label: 'Suppliers', icon: <Boxes className="size-[18px]" />, show: has('suppliers.view', 'suppliers.manage') },
      ],
    },
    {
      group: 'Administration',
      items: [
        { to: '/staff', label: 'Staff', icon: <Users className="size-[18px]" />, show: has('users.view') },
        { to: '/roles', label: 'Roles & permissions', icon: <ShieldCheck className="size-[18px]" />, show: has('roles.manage') },
        { to: '/branches', label: 'Branches', icon: <Store className="size-[18px]" />, show: has('branches.manage') },
        { to: '/fiscal', label: 'ZIMRA fiscalisation', icon: <BadgeCheck className="size-[18px]" />, show: has('fiscal.manage') },
        { to: '/settings', label: 'Business settings', icon: <Settings2 className="size-[18px]" />, show: has('settings.manage') },
        { to: '/activity', label: 'Security and activity', icon: <Activity className="size-[18px]" />, show: has('dashboard.view') && has('users.view') },
        { to: '/audit', label: 'Audit log', icon: <ScrollText className="size-[18px]" />, show: has('audit.view') },
      ],
    },
  ];

  const displayName = me.business.tradingName ?? me.business.name;

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[260px_1fr]">
      {/* Navigation rail */}
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-40 flex w-[272px] flex-col bg-ledger-800 text-white transition-transform lg:sticky lg:top-0 lg:h-dvh lg:w-auto lg:translate-x-0',
          navOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-label="Main navigation"
      >
        <div className="flex items-center justify-between px-5 pt-5 pb-4">
          <Wordmark />
          <button className="rounded-md p-1.5 text-white/70 hover:bg-white/10 lg:hidden" onClick={() => setNavOpen(false)} aria-label="Close menu">
            <X className="size-5" />
          </button>
        </div>
        <div className="mx-3 mb-4 rounded-[10px] bg-ledger-900/70 px-3.5 py-3">
          <p className="truncate font-medium leading-tight" title={displayName}>
            {displayName}
          </p>
          <p className="text-sm text-white/60">{BUSINESS_TYPE_LABELS[me.business.businessType]}</p>
        </div>
        <nav className="min-h-0 flex-1 overflow-y-auto px-3 pb-6">
          {nav.map((section) => {
            const items = section.items.filter((i) => i.show);
            if (!items.length) return null;
            return (
              <div key={section.group} className="mb-5">
                <p className="px-3 pb-1.5 text-[13px] text-white/45">{section.group}</p>
                <ul className="space-y-0.5">
                  {items.map((item) => (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.to === '/'}
                        className={({ isActive }) =>
                          clsx(
                            'relative flex items-center gap-3 rounded-[8px] px-3 py-2 text-[15px] transition-colors',
                            isActive
                              ? 'bg-white/[0.09] text-white before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[3px] before:rounded-full before:bg-gold'
                              : 'text-white/75 hover:bg-white/[0.06] hover:text-white',
                          )
                        }
                      >
                        {item.icon}
                        {item.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>
        <p className="shrink-0 border-t border-white/10 px-6 py-3 text-xs text-white/40">Tengesa 1.0</p>
      </aside>
      {navOpen && <div className="fixed inset-0 z-30 bg-ledger-950/40 lg:hidden" onClick={() => setNavOpen(false)} aria-hidden />}

      <div className="flex min-w-0 flex-col">
        <TopBar onMenu={() => setNavOpen(true)} />
        <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-6 sm:px-6 lg:px-10 lg:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export function Wordmark({ dark }: { dark?: boolean }) {
  return (
    <div className="flex items-center gap-2.5">
      <svg viewBox="0 0 32 32" className="size-8" aria-hidden>
        <rect width="32" height="32" rx="7" fill={dark ? '#12352b' : '#0f2d24'} />
        <path d="M8 10h16M16 10v14" stroke="#e6b23c" strokeWidth="3.2" strokeLinecap="round" />
        <circle cx="23.5" cy="21.5" r="2.4" fill="#e6b23c" />
      </svg>
      <span className={clsx('text-xl font-semibold tracking-tight', dark ? 'text-ink' : 'text-white')}>tengesa</span>
    </div>
  );
}

function TopBar({ onMenu }: { onMenu: () => void }) {
  const { me } = useSession();
  const { branch, select } = useCurrentBranch();
  const [menuOpen, setMenuOpen] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [menuOpen]);

  const signOut = async () => {
    try {
      await api.post('/api/auth/logout');
    } finally {
      qc.clear();
      qc.setQueryData(['me'], null);
      navigate('/');
    }
  };

  const initials = me.user.fullName
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-line bg-paper/90 px-4 backdrop-blur sm:px-6 lg:px-10">
      <button className="-ml-1 rounded-md p-2 text-ink hover:bg-ink/5 lg:hidden" onClick={onMenu} aria-label="Open menu">
        <Menu className="size-5" />
      </button>

      {me.branches.length > 0 && (
        <label className="flex items-center gap-2 text-sm text-muted">
          <Building2 className="size-4" aria-hidden />
          <span className="sr-only">Working at branch</span>
          {me.branches.length === 1 ? (
            <span className="font-medium text-ink">{branch?.name}</span>
          ) : (
            <select
              value={branch?.id ?? ''}
              onChange={(e) => select(e.target.value)}
              className="h-9 rounded-[8px] border border-line bg-surface pl-2 pr-7 text-[15px] font-medium text-ink"
            >
              {me.branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          )}
        </label>
      )}

      <div className="relative ml-auto">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((v) => !v);
          }}
          className="flex items-center gap-2.5 rounded-full py-1 pl-1 pr-2 hover:bg-ink/5"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <span className="grid size-8 place-items-center rounded-full bg-ledger-100 text-sm font-semibold text-ledger-700">{initials}</span>
          <span className="hidden text-left sm:block">
            <span className="block text-sm font-medium leading-tight">{me.user.fullName}</span>
            <span className="block text-xs text-muted">{me.user.roleName}</span>
          </span>
          <ChevronDown className="size-4 text-muted" aria-hidden />
        </button>
        {menuOpen && (
          <div role="menu" className="absolute right-0 top-12 w-56 overflow-hidden rounded-[10px] border border-line bg-surface py-1 shadow-xl">
            <MenuLink icon={<UserRound className="size-4" />} onClick={() => navigate('/account')}>
              My account
            </MenuLink>
            <MenuLink icon={<KeyRound className="size-4" />} onClick={() => navigate('/account#password')}>
              Change password
            </MenuLink>
            <div className="my-1 border-t border-line" />
            <MenuLink icon={<LogOut className="size-4" />} onClick={signOut}>
              Sign out
            </MenuLink>
          </div>
        )}
      </div>
    </header>
  );
}

function MenuLink({ icon, children, onClick }: { icon: ReactNode; children: ReactNode; onClick: () => void }) {
  return (
    <button role="menuitem" onClick={onClick} className="flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-[15px] hover:bg-paper">
      <span className="text-muted">{icon}</span>
      {children}
    </button>
  );
}
